import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import apiConfig from '../../../apiConfig';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Add, Edit, Visibility, Delete, Payment, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, Print, Search
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Divider, Grid,
  FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import PaymentReceiptsModal from './PaymentReceiptsModal';
import './PaymentReceiptsIndex.css';
import jsPDF from 'jspdf';
import 'jspdf-autotable';


const PaymentReceiptsIndex = () => {
  // Session validation
  useSessionValidation();

  const history = useHistory();
  const { companyId, branchId, token, userId, permissions } = useAuth();

  const hasCreatePermission = permissions?.includes('Create Master');
  const hasEditPermission = permissions?.includes('Edit Master');
  const hasDeletePermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [paymentReceipts, setPaymentReceipts] = useState([]);
  const [masters, setMasters] = useState([]);
  const [banks, setBanks] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [vatRates, setVatRates] = useState([]);
  const [companyData, setCompanyData] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all'); // payment or receipt
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [paymentReceiptDetails, setPaymentReceiptDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete/Activate confirmation modals
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  // Cheque status update modal
  const [chequeStatusModalOpen, setChequeStatusModalOpen] = useState(false);
  const [selectedChequeItem, setSelectedChequeItem] = useState(null);
  const [newChequeStatus, setNewChequeStatus] = useState(1);
  const [bulkChequeStatusModalOpen, setBulkChequeStatusModalOpen] = useState(false);
  const [selectedChequeItems, setSelectedChequeItems] = useState([]);
  const [individualChequeStatuses, setIndividualChequeStatuses] = useState({}); // Store individual status for each item

  // Drill-down states for animated expansion - REMOVED (not needed)

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchAllData();
    }
  }, [companyId, token, fromDate, toDate]);

  // Check for viewId query parameter and open modal
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewId = params.get('viewId');

    if (viewId && paymentReceipts.length > 0 && !openModal) {
      const item = paymentReceipts.find(p => p.Id === parseInt(viewId));
      if (item) {
        handleOpenModal('view', parseInt(viewId));
        history.replace(window.location.pathname);
      }
    }
  }, [paymentReceipts, openModal]);

  // Fetch functions
  const fetchPaymentReceipts = async () => {
    try {
      const activeResponse = await fetchData('PaymentAndReceiptApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: item.IsActive ?? true }));

      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PaymentAndReceiptApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive payment receipts:', inactiveError);
      }

      setPaymentReceipts([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching payment receipts:', error);
      setPaymentReceipts([]);
    }
  };

  const fetchMasters = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/Master/${companyId}`, token);
      setMasters(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching masters:', error);
      setMasters([]);
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await fetchData('BankApi', `/${companyId}`, token);
      setBanks(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching banks:', error);
      setBanks([]);
    }
  };

  const fetchPurchases = async (masterId = null) => {
    if (!masterId || masterId === 0) {
      setPurchases([]);
      return;
    }

    try {
      console.log('Fetching purchases for masterId:', masterId);
      const endpoint = `/GetAllPendingPurchase/${companyId}/${masterId}`;
      const response = await fetchData('PaymentAndReceiptApi', endpoint, token);
      const purchaseData = response.response_data || response || [];
      console.log('Fetched purchases:', purchaseData.length, 'items');
      setPurchases(purchaseData);
    } catch (error) {
      console.error('Error fetching purchases:', error);
      setPurchases([]);
    }
  };

  const fetchSales = async (masterId = null) => {
    if (!masterId || masterId === 0) {
      setSales([]);
      return;
    }

    try {
      console.log('Fetching sales for masterId:', masterId);
      const endpoint = `/GetAllPendingSales/${companyId}/${masterId}`;
      const response = await fetchData('PaymentAndReceiptApi', endpoint, token);
      const salesData = response.response_data || response || [];
      console.log('Fetched sales:', salesData.length, 'items');
      setSales(salesData);
    } catch (error) {
      console.error('Error fetching sales:', error);
      setSales([]);
    }
  };

  // Fetch bills based on transaction type (Payment or Receipt)
  const fetchBillsForMaster = async (masterId, isType) => {
    if (!masterId || masterId === 0) {
      setPurchases([]);
      setSales([]);
      return;
    }

    // If Payment (isType = true), fetch Purchase bills
    // If Receipt (isType = false), fetch Sales bills
    if (isType) {
      await fetchPurchases(masterId);
      setSales([]); // Clear sales when fetching purchases
    } else {
      await fetchSales(masterId);
      setPurchases([]); // Clear purchases when fetching sales
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentModes/${companyId}`, token);
      const paymentMethodsData = response.response_data || response || [];

      // Debug logging to check API response
      console.log('=== PAYMENT METHODS API RESPONSE ===');
      console.log('Raw response:', response);
      console.log('Payment methods data:', paymentMethodsData);
      console.log('Payment methods structure:');
      paymentMethodsData.forEach((method, index) => {
        console.log(`Method ${index + 1}:`, {
          Id: method.Id,
          Name: method.Name,
          IsActive: method.IsActive,
          FullObject: method
        });
      });
      console.log('=== END PAYMENT METHODS DEBUG ===');

      setPaymentMethods(paymentMethodsData);
    } catch (error) {
      console.error('Error fetching payment methods:', error);

      // Fallback to default payment methods if API fails
      const fallbackMethods = [
        { Id: 1, Name: 'Cash', IsActive: true },
        { Id: 2, Name: 'Cheque', IsActive: true },
        { Id: 3, Name: 'Online Transfer', IsActive: true }
      ];

      console.log('=== USING FALLBACK PAYMENT METHODS ===');
      console.log('Fallback methods:', fallbackMethods);
      console.log('=== END FALLBACK DEBUG ===');

      setPaymentMethods(fallbackMethods);
    }
  };

  const fetchVatRates = async () => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/GetVATList/${companyId}`, token);
      setVatRates(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching VAT rates:', error);
      // Fallback to default VAT rate of 5% if API fails
      setVatRates([
        { Id: 1, Rate: 5, Name: '5%', IsActive: true }
      ]);
    }
  };

  const fetchCompanyData = async () => {
    try {
      const response = await fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token);
      setCompanyData(response.response_data || response || null);
    } catch (error) {
      console.error('Error fetching company data:', error);
      setCompanyData(null);
    }
  };

  const fetchMaxBillNo = async (isType = true) => {
    try {
      const isTypeString = isType.toString();
      const response = await fetchData('PaymentAndReceiptApi', `/MaxBillno/${branchId || 1}/${companyId}/${isTypeString}`, token);
      const data = response.response_data || response || {};
      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';
      return { billNumber, billPrefix, finYear };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '' };
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setLoadingMessage('Fetching payment receipts...');
    try {
      await Promise.allSettled([
        fetchPaymentReceipts(),
        fetchMasters(),
        fetchBanks(),
        fetchPurchases(),
        fetchSales(),
        fetchPaymentMethods(),
        fetchVatRates(),
        fetchCompanyData()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch specific payment receipt details by ID
  const fetchPaymentReceiptDetails = async (paymentReceiptId) => {
    try {
      const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${paymentReceiptId}`, token);
      return response.response_data || response || null;
    } catch (error) {
      console.error('Error fetching payment receipt details:', error);
      // Try alternative endpoint if GetById doesn't work
      try {
        const altResponse = await fetchData('PaymentAndReceiptApi', `/${paymentReceiptId}`, token);
        return altResponse.response_data || altResponse || null;
      } catch (altError) {
        console.error('Error with alternative endpoint:', altError);
        return null;
      }
    }
  };

  // Print Payment/Receipt handler - Professional jsPDF Format
  const handlePrintPaymentReceipt = async (paymentReceiptId) => {
    setLoading(true);
    setLoadingMessage('Generating PDF...');

    try {
      const freshData = await fetchPaymentReceiptDetails(paymentReceiptId);

      if (!freshData) {
        setError('Failed to fetch payment receipt details for printing.');
        setLoading(false);
        return;
      }

      const paymentData = freshData.PaymentAndReceipt || freshData;
      const purchaseDetails = freshData.PaymentAndReceiptPurchase || [];
      const saleDetails = freshData.PaymentAndReceiptSale || [];

      // Get master details
      const master = masters.find(m => m.Id === paymentData.MasterId);
      const masterName = paymentData.MasterName || master?.Name || '-';
      const masterAddress = master?.Address || '';
      const masterVAT = master?.VATNumber || '';
      const masterContact = master?.ContactNumber || master?.Mobile || '';

      // Get bank details if applicable
      const bank = banks.find(b => b.Id === paymentData.BankId);
      const bankName = bank?.Name || '';

      // Get payment method
      const paymentMethod = paymentMethods.find(pm => pm.Id === paymentData.PaymentType);
      const paymentMethodName = paymentMethod?.Name || 'Cash';

      // Company details
      const companyNameDisplay = companyData?.Name || 'Company Name';
      const companyAddress = companyData?.Address || '';
      const companyVAT = companyData?.VATNumber || '';
      const companyContact = companyData?.ContactNumber || '';
      const companyEmail = companyData?.Email || '';

      const isPayment = paymentData.IsType;
      const documentType = isPayment ? 'PAYMENT VOUCHER' : 'RECEIPT VOUCHER';
      const partyLabel = isPayment ? 'Paid To' : 'Received From';

      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.width;
      const pageHeight = pdf.internal.pageSize.height;
      const margin = 15;
      let yPosition = margin;

      // Enhanced color scheme
      const primaryColor = isPayment ? [220, 38, 127] : [16, 185, 129]; // Pink for Payment, Green for Receipt
      const secondaryColor = isPayment ? [190, 24, 93] : [5, 150, 105];
      const accentColor = [59, 130, 246]; // Blue accent
      const textColor = [17, 24, 39];
      const lightGray = [248, 250, 252];
      const mediumGray = [148, 163, 184];
      const darkGray = [71, 85, 105];

      // Helper function to add styled text
      const addStyledText = (text, x, y, options = {}) => {
        const {
          fontSize = 10,
          fontStyle = 'normal',
          color = textColor,
          align = 'left',
          maxWidth = null
        } = options;

        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', fontStyle);
        pdf.setTextColor(...color);

        if (maxWidth) {
          const lines = pdf.splitTextToSize(text, maxWidth);
          pdf.text(lines, x, y, { align });
          return y + (lines.length * fontSize * 0.35);
        } else {
          pdf.text(text, x, y, { align });
          return y + (fontSize * 0.35);
        }
      };

      // Helper function to add rectangle with border
      const addStyledRect = (x, y, width, height, options = {}) => {
        const {
          fillColor = null,
          borderColor = [0, 0, 0],
          borderWidth = 0.5
        } = options;

        if (fillColor) {
          pdf.setFillColor(...fillColor);
          pdf.rect(x, y, width, height, 'F');
        }

        pdf.setDrawColor(...borderColor);
        pdf.setLineWidth(borderWidth);
        pdf.rect(x, y, width, height, 'S');
      };

      // Company Header with styling
      addStyledRect(margin, yPosition, pageWidth - 2 * margin, 25, {
        fillColor: lightGray,
        borderColor: primaryColor,
        borderWidth: 1
      });

      yPosition += 5;
      yPosition = addStyledText(companyNameDisplay, pageWidth / 2, yPosition, {
        fontSize: 16,
        fontStyle: 'bold',
        color: primaryColor,
        align: 'center'
      });

      if (companyAddress) {
        yPosition = addStyledText(companyAddress, pageWidth / 2, yPosition + 3, {
          fontSize: 9,
          align: 'center',
          maxWidth: pageWidth - 2 * margin - 10
        });
      }

      const contactInfo = [
        companyVAT ? `VAT: ${companyVAT}` : '',
        companyContact ? `Tel: ${companyContact}` : '',
        companyEmail ? `Email: ${companyEmail}` : ''
      ].filter(Boolean).join(' | ');

      if (contactInfo) {
        yPosition = addStyledText(contactInfo, pageWidth / 2, yPosition + 2, {
          fontSize: 8,
          align: 'center'
        });
      }

      yPosition += 10;

      // Document Title with colored background
      addStyledRect(margin, yPosition, pageWidth - 2 * margin, 12, {
        fillColor: primaryColor,
        borderColor: primaryColor,
        borderWidth: 1
      });

      yPosition = addStyledText(documentType, pageWidth / 2, yPosition + 8, {
        fontSize: 14,
        fontStyle: 'bold',
        color: [255, 255, 255],
        align: 'center'
      });

      yPosition += 8;

      // Voucher Information Section
      const leftColWidth = (pageWidth - 2 * margin) * 0.65;
      const rightColWidth = (pageWidth - 2 * margin) * 0.35;

      // Party Information Box
      addStyledRect(margin, yPosition, leftColWidth, 35, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let partyYPos = yPosition + 5;
      partyYPos = addStyledText(partyLabel, margin + 5, partyYPos, {
        fontSize: 10,
        fontStyle: 'bold',
        color: primaryColor
      });

      partyYPos = addStyledText(masterName, margin + 15, partyYPos + 3, {
        fontSize: 11,
        fontStyle: 'bold'
      });

      if (masterAddress) {
        partyYPos = addStyledText(masterAddress, margin + 15, partyYPos + 2, {
          fontSize: 9,
          maxWidth: leftColWidth - 25
        });
      }

      if (masterVAT) {
        partyYPos = addStyledText(`VAT: ${masterVAT}`, margin + 15, partyYPos + 2, {
          fontSize: 9
        });
      }

      if (masterContact) {
        addStyledText(`Contact: ${masterContact}`, margin + 15, partyYPos + 2, {
          fontSize: 9
        });
      }

      // Voucher Details Box
      addStyledRect(margin + leftColWidth, yPosition, rightColWidth, 35, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let detailsYPos = yPosition + 5;
      const detailsX = margin + leftColWidth + 5;

      detailsYPos = addStyledText(`Voucher No: ${paymentData.BillNumber}`, detailsX, detailsYPos, {
        fontSize: 9,
        fontStyle: 'bold'
      });

      detailsYPos = addStyledText(`Date: ${formatDate(paymentData.BillDate)}`, detailsX, detailsYPos + 3, {
        fontSize: 9
      });

      detailsYPos = addStyledText(`FY: ${paymentData.FinYear || new Date().getFullYear()}`, detailsX, detailsYPos + 3, {
        fontSize: 9
      });

      detailsYPos = addStyledText(`Mode: ${paymentMethodName}`, detailsX, detailsYPos + 3, {
        fontSize: 9
      });

      if (paymentData.ChequeNumber) {
        detailsYPos = addStyledText(`Ref: ${paymentData.ChequeNumber}`, detailsX, detailsYPos + 3, {
          fontSize: 9
        });
      }

      if (bankName) {
        addStyledText(`Bank: ${bankName}`, detailsX, detailsYPos + 3, {
          fontSize: 9
        });
      }

      yPosition += 45;

      // Transaction Details Table
      const tableData = [];
      let totalAmount = parseFloat(paymentData.Amount) || 0;

      if (isPayment && purchaseDetails.length > 0) {
        purchaseDetails.forEach((d, idx) => {
          tableData.push([
            (idx + 1).toString(),
            'Purchase Bill Payment',
            `Purchase ID: ${d.PurchaseId || '-'}`,
            formatCurrency(d.Amount || 0)
          ]);
        });
      } else if (!isPayment && saleDetails.length > 0) {
        saleDetails.forEach((d, idx) => {
          tableData.push([
            (idx + 1).toString(),
            'Sale Bill Receipt',
            `Sale ID: ${d.SaleId || '-'}`,
            formatCurrency(d.Amount || 0)
          ]);
        });
      } else {
        tableData.push([
          '1',
          isPayment ? 'General Payment' : 'General Receipt',
          paymentData.Remarks || `Direct ${isPayment ? 'Payment' : 'Receipt'}`,
          formatCurrency(totalAmount)
        ]);
      }

      // Create table with autoTable
      if (typeof pdf.autoTable === 'function') {
        pdf.autoTable({
          startY: yPosition,
          head: [['Sr.No', 'Particulars', 'Description', 'Amount']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: primaryColor,
            textColor: [255, 255, 255],
            fontSize: 11,
            fontStyle: 'bold',
            halign: 'center',
            cellPadding: 4
          },
          bodyStyles: {
            fontSize: 10,
            textColor: textColor,
            cellPadding: 3
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: 20 },
            1: { halign: 'left', cellWidth: 50 },
            2: { halign: 'left', cellWidth: 75 },
            3: { halign: 'right', cellWidth: 30, fontStyle: 'bold' }
          },
          margin: { left: margin, right: margin },
          tableLineColor: primaryColor,
          tableLineWidth: 0.8,
          alternateRowStyles: {
            fillColor: [248, 250, 252]
          }
        });

        yPosition = pdf.lastAutoTable ? pdf.lastAutoTable.finalY + 10 : yPosition + 100;
      } else {
        console.error('autoTable plugin not loaded properly');
        yPosition += 100; // Fallback positioning
      }


      // Amount Summary Section
      const summaryBoxHeight = 40;
      const leftSummaryWidth = (pageWidth - 2 * margin) * 0.6;
      const rightSummaryWidth = (pageWidth - 2 * margin) * 0.4;

      // Amount in Words Box
      addStyledRect(margin, yPosition, leftSummaryWidth, summaryBoxHeight, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let wordsYPos = yPosition + 5;
      wordsYPos = addStyledText('Amount in Words:', margin + 5, wordsYPos, {
        fontSize: 10,
        fontStyle: 'bold',
        color: primaryColor
      });

      wordsYPos = addStyledText(`Rupees ${numberToWords(totalAmount)} Only`, margin + 5, wordsYPos + 3, {
        fontSize: 9,
        fontStyle: 'italic',
        maxWidth: leftSummaryWidth - 10
      });

      if (paymentData.Remarks) {
        wordsYPos = addStyledText('Remarks:', margin + 5, wordsYPos + 5, {
          fontSize: 9,
          fontStyle: 'bold'
        });
        addStyledText(paymentData.Remarks, margin + 5, wordsYPos + 2, {
          fontSize: 8,
          maxWidth: leftSummaryWidth - 10
        });
      }

      // Amount Breakdown Box
      addStyledRect(margin + leftSummaryWidth, yPosition, rightSummaryWidth, summaryBoxHeight, {
        fillColor: [255, 255, 255],
        borderColor: primaryColor,
        borderWidth: 0.8
      });

      let amountYPos = yPosition + 5;
      const amountX = margin + leftSummaryWidth + 5;

      // Amount breakdown table
      const amountData = [
        ['Basic Amount:', formatCurrency(paymentData.Amount || 0)]
      ];

      if (paymentData.VATAmount > 0) {
        amountData.push(['VAT (5%):', formatCurrency(paymentData.VATAmount)]);
      }
      if (paymentData.RoundOff !== 0) {
        amountData.push(['Round Off:', formatCurrency(paymentData.RoundOff || 0)]);
      }

      amountData.forEach(([label, amount]) => {
        addStyledText(label, amountX, amountYPos, { fontSize: 9 });
        addStyledText(amount, amountX + rightSummaryWidth - 15, amountYPos, {
          fontSize: 9,
          align: 'right'
        });
        amountYPos += 4;
      });

      // Total Amount with highlight
      addStyledRect(amountX - 2, amountYPos - 1, rightSummaryWidth - 6, 6, {
        fillColor: primaryColor
      });

      addStyledText('Total Amount:', amountX, amountYPos + 2, {
        fontSize: 10,
        fontStyle: 'bold',
        color: [255, 255, 255]
      });
      addStyledText(formatCurrency(paymentData.TotalAmount || totalAmount), amountX + rightSummaryWidth - 15, amountYPos + 2, {
        fontSize: 10,
        fontStyle: 'bold',
        color: [255, 255, 255],
        align: 'right'
      });

      yPosition += summaryBoxHeight + 15;

      // Signature Section
      const signatureHeight = 25;
      const signatureWidth = (pageWidth - 2 * margin) / 3;

      ['PREPARED BY', 'CHECKED BY', 'AUTHORIZED SIGNATORY'].forEach((title, index) => {
        const x = margin + (index * signatureWidth);
        addStyledRect(x, yPosition, signatureWidth, signatureHeight, {
          fillColor: [255, 255, 255],
          borderColor: primaryColor,
          borderWidth: 0.8
        });

        addStyledText(title, x + signatureWidth / 2, yPosition + signatureHeight - 3, {
          fontSize: 9,
          fontStyle: 'bold',
          align: 'center'
        });
      });

      yPosition += signatureHeight + 10;

      // Footer
      addStyledText('This is a computer generated document and does not require signature.', pageWidth / 2, yPosition, {
        fontSize: 8,
        color: darkGray,
        align: 'center'
      });

      // Add watermark
      pdf.setTextColor(200, 200, 200);
      pdf.setFontSize(60);
      pdf.text(isPayment ? 'PAYMENT' : 'RECEIPT', pageWidth / 2, pageHeight / 2, {
        align: 'center',
        angle: 45
      });

      // Direct Print instead of download
      pdf.autoPrint();

      // Open in new window for printing
      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);

      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          // Clean up the URL after printing
          setTimeout(() => {
            URL.revokeObjectURL(pdfUrl);
          }, 1000);
        };
      } else {
        // Fallback if popup is blocked
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `${documentType.replace(' ', '_')}_${paymentData.BillNumber}_${formatDate(paymentData.BillDate).replace(/\//g, '-')}.pdf`;
        link.click();
        URL.revokeObjectURL(pdfUrl);
      }

      setSuccess(`Document prepared for printing: ${documentType} ${paymentData.BillNumber}`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Failed to generate PDF.');
    }

    setLoading(false);
  };

  // Helper function to convert number to words (basic implementation)
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convertHundreds = (n) => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        return result;
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result;
    };

    let result = '';
    const crores = Math.floor(num / 10000000);
    if (crores > 0) {
      result += convertHundreds(crores) + 'Crore ';
      num %= 10000000;
    }

    const lakhs = Math.floor(num / 100000);
    if (lakhs > 0) {
      result += convertHundreds(lakhs) + 'Lakh ';
      num %= 100000;
    }

    const thousands = Math.floor(num / 1000);
    if (thousands > 0) {
      result += convertHundreds(thousands) + 'Thousand ';
      num %= 1000;
    }

    if (num > 0) {
      result += convertHundreds(num);
    }

    return result.trim();
  };

  // Handle modal actions
  const handleOpenModal = async (action, item = null) => {
    setModalAction(action);
    setSelectedItem(item);

    if (action === 'create') {
      const defaultIsType = true; // Default to Payment, can be changed in modal
      const maxBillData = await fetchMaxBillNo(defaultIsType);
      const billNumber = maxBillData.billPrefix && maxBillData.finYear ?
        `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}` :
        maxBillData.billNumber.toString();

      setFormData({
        Id: 0,
        CompanyId: companyId,
        BranchId: branchId,
        BillNumber: billNumber,
        BillDate: new Date().toISOString().split('T')[0],
        MasterId: '',
        SubHeadId: '',
        PartyId: '',
        IsType: true, // true for Payment, false for Receipt
        IsVAT: false,
        VATHeadId: '',
        IsSaleAndPurchase: false,
        PaymentType: 1, // 1=Cash, 2=Cheque, 3=Online
        ChequeDate: '',
        ChequeNumber: '',
        BankId: null,
        chequeDetails: '',
        ChequeStatusId: 1,
        Amount: 0,
        VATRate: 0,
        VATAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        Remarks: '',
        IsActive: true
      });
      setPaymentReceiptDetails([]);
    } else if (action === 'edit' || action === 'view') {
      setModalLoading(true);
      try {
        // Fetch complete payment receipt data from API - following JournalEntry pattern
        console.log('Fetching payment receipt data for ID:', item.Id);
        const response = await fetchData('PaymentAndReceiptApi', `/PaymentData/${item.Id}`, token);
        const paymentReceiptData = response.response_data || response;
        console.log('Fetched payment receipt data:', paymentReceiptData);

        const paymentData = paymentReceiptData.PaymentAndReceipt;

        setFormData({
          ...paymentData,
          BillDate: paymentData.BillDate ? paymentData.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          ChequeDate: paymentData.ChequeDate ? paymentData.ChequeDate.split('T')[0] : '',
          MasterId: paymentData.MasterId || ''
        });

        // Set payment receipt details from the API response
        const purchaseDetails = paymentReceiptData.PaymentAndReceiptPurchase || [];
        const saleDetails = paymentReceiptData.PaymentAndReceiptSale || [];

        if (paymentData.IsType && purchaseDetails.length > 0) {
          // Payment - show purchase details
          const mappedDetails = purchaseDetails.map(p => ({
            Id: p.PurchaseId,
            Description: p.PurchaseDescription || `Purchase - ${p.PurchaseId}`,
            Amount: p.PurchaseAmount || 0,
            PaidAmount: p.Amount || 0,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: p.BillDate || paymentData.BillDate,
            Type: 'Purchase',
            PaymentAmount: p.Amount || 0,
            IsSelected: true
          }));
          setPaymentReceiptDetails(mappedDetails);
        } else if (!paymentData.IsType && saleDetails.length > 0) {
          // Receipt - show sale details
          const mappedDetails = saleDetails.map(s => ({
            Id: s.SaleId,
            Description: s.SaleDescription || `Sale - ${s.SaleId}`,
            Amount: s.SaleAmount || 0,
            PaidAmount: s.Amount || 0,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: s.BillDate || paymentData.BillDate,
            Type: 'Sale',
            PaymentAmount: s.Amount || 0,
            IsSelected: true
          }));
          setPaymentReceiptDetails(mappedDetails);
        } else {
          // No specific purchase/sale details - create a general entry
          setPaymentReceiptDetails([{
            Id: paymentData.Id,
            Description: `${paymentData.IsType ? 'Payment' : 'Receipt'} - ${paymentData.BillNumber}`,
            Amount: paymentData.Amount,
            PaidAmount: paymentData.Amount,
            BalanceAmount: 0,
            PendingAmount: 0,
            BillDate: paymentData.BillDate,
            Type: paymentData.IsType ? 'Payment' : 'Receipt',
            PaymentAmount: paymentData.Amount,
            IsSelected: true,
            isDirectPayment: true
          }]);
        }

      } catch (error) {
        console.error('Error fetching payment receipt details:', error);
        setError('Failed to fetch payment receipt details.');
        // Fallback to using the item data from the list
        setFormData({
          ...item,
          BillDate: item.BillDate ? item.BillDate.split('T')[0] : new Date().toISOString().split('T')[0],
          ChequeDate: item.ChequeDate ? item.ChequeDate.split('T')[0] : '',
          MasterId: item.MasterId || ''
        });

        // Create a basic entry for fallback
        setPaymentReceiptDetails([{
          Id: item.Id,
          Description: `${item.IsType ? 'Payment' : 'Receipt'} - ${item.BillNumber}`,
          Amount: item.Amount,
          PaidAmount: item.Amount,
          BalanceAmount: 0,
          PendingAmount: 0,
          BillDate: item.BillDate,
          Type: item.IsType ? 'Payment' : 'Receipt',
          PaymentAmount: item.Amount,
          IsSelected: true,
          isDirectPayment: true
        }]);
      }
      setModalLoading(false);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedItem(null);
    setFormData({});
    setPaymentReceiptDetails([]);
    window.location.reload();
  };

  // Handle save
  const handleSave = async () => {
    setModalLoading(true);
    try {
      const dataToSend = {
        PaymentAndReceipt: {
          Id: modalAction === 'edit' ? formData.Id : 0,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: parseInt(formData.MasterId),
          SubHeadId: formData.SubHeadId || 0,
          PartyId: formData.PartyId || formData.MasterId, // Use MasterId as PartyId if PartyId not set
          BillDate: formData.BillDate,
          BillNumber: parseInt(formData.BillNumber) || 0,
          FinYear: formData.FinYear || new Date().getFullYear().toString(),
          IsType: formData.IsType, // true for Payment, false for Receipt
          IsVAT: formData.IsVAT || false,
          VATHeadId: formData.VATHeadId || 0,
          IsSaleAndPurchase: formData.IsSaleAndPurchase || true,
          PaymentType: formData.PaymentType || 1, // 1=Cash, 2=Cheque, etc.
          ChequeDate: formData.ChequeDate || null,
          ChequeNumber: formData.ChequeNumber || null,
          BankId: formData.BankId || null,
          chequeDetails: formData.chequeDetails || null,
          ChequeStatusId: formData.ChequeStatusId || 1,
          Amount: parseFloat(formData.Amount) || 0,
          VATRate: parseFloat(formData.VATRate) || 0,
          VATAmount: parseFloat(formData.VATAmount) || 0,
          RoundOff: parseFloat(formData.RoundOff) || 0,
          TotalAmount: parseFloat(formData.TotalAmount) || parseFloat(formData.Amount) || 0,
          Remarks: formData.Remarks || null,
          IsActive: true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          UpdatedBy: modalAction === 'edit' ? userId : undefined,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : formData.CreatedDate,
          UpdatedDate: modalAction === 'edit' ? new Date().toISOString() : undefined
        }
      };

      // Add purchase/sale details if any are selected
      const selectedDetails = paymentReceiptDetails.filter(d => d.IsSelected && d.PaymentAmount > 0);
      if (selectedDetails.length > 0) {
        if (formData.IsType) {
          // Payment - add purchase details
          dataToSend.PaymentAndReceiptPurchase = selectedDetails.map(d => ({
            Id: 0,
            CompanyId: companyId,
            BranchId: branchId,
            MasterId: parseInt(formData.MasterId),
            PurchaseId: d.Id,
            PaymentAndReceiptId: 0, // Will be set by backend after PaymentAndReceipt is created
            Amount: parseFloat(d.PaymentAmount) || 0,
            IsActive: true,
            CreatedBy: userId,
            UpdatedBy: null,
            CreatedDate: new Date().toISOString(),
            UpdatedDate: null
          }));
        } else {
          // Receipt - add sale details
          dataToSend.PaymentAndReceiptSale = selectedDetails.map(d => ({
            Id: 0,
            CompanyId: companyId,
            BranchId: branchId,
            MasterId: parseInt(formData.MasterId),
            SaleId: d.Id,
            PaymentAndReceiptId: 0, // Will be set by backend after PaymentAndReceipt is created
            Amount: parseFloat(d.PaymentAmount) || 0,
            IsActive: true,
            CreatedBy: userId,
            UpdatedBy: null,
            CreatedDate: new Date().toISOString(),
            UpdatedDate: null
          }));
        }
      }

      console.log('Saving Payment/Receipt data:', dataToSend);

      if (modalAction === 'create') {
        await postData('PaymentAndReceiptApi', '', dataToSend, token);
        setSuccess(`${formData.IsType ? 'Payment' : 'Receipt'} created successfully!`);
      } else {
        await updateData('PaymentAndReceiptApi', formData.Id, dataToSend, token);
        setSuccess(`${formData.IsType ? 'Payment' : 'Receipt'} updated successfully!`);
      }

      handleCloseModal();
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error saving payment receipt:', error);
      setError(`Failed to save ${formData.IsType ? 'payment' : 'receipt'}.`);
    }
    setModalLoading(false);
  };

  // Delete modal handlers
  const handleOpenDeleteModal = (id, billNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(billNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  // Handle delete
  const handleDelete = async () => {
    try {
      await deleteData('PaymentAndReceiptApi', deleteItemId, token);
      setSuccess('Payment/Receipt deleted successfully!');
      setDeleteModalOpen(false);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error deleting payment receipt:', error);
      setError('Failed to delete payment receipt.');
    }
  };

  // Activate modal handlers
  const handleOpenActivateModal = (id, billNo) => {
    setActivateItemId(id);
    setActivateItemNo(billNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  // Handle activate
  const handleActivate = async () => {
    try {
      await activateData('PaymentAndReceiptApi', activateItemId, token);
      setSuccess('Payment/Receipt activated successfully!');
      setActivateModalOpen(false);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error activating payment receipt:', error);
      setError('Failed to activate payment receipt.');
    }
  };

  // Handle cheque status update
  const handleUpdateChequeStatus = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Updating cheque status...');

      const updateData = {
        PaymentAndReceipt: {
          Id: selectedChequeItem.Id,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: selectedChequeItem.MasterId,
          SubHeadId: selectedChequeItem.SubHeadId || 0,
          PartyId: selectedChequeItem.PartyId || selectedChequeItem.MasterId,
          BillDate: selectedChequeItem.BillDate,
          BillNumber: selectedChequeItem.BillNumber,
          FinYear: selectedChequeItem.FinYear || new Date().getFullYear().toString(),
          IsType: selectedChequeItem.IsType,
          IsVAT: selectedChequeItem.IsVAT || false,
          VATHeadId: selectedChequeItem.VATHeadId || 0,
          IsSaleAndPurchase: selectedChequeItem.IsSaleAndPurchase || true,
          PaymentType: selectedChequeItem.PaymentType,
          ChequeDate: selectedChequeItem.ChequeDate,
          ChequeNumber: selectedChequeItem.ChequeNumber,
          BankId: selectedChequeItem.BankId,
          chequeDetails: selectedChequeItem.chequeDetails,
          ChequeStatusId: newChequeStatus, // This is the key update
          Amount: selectedChequeItem.Amount,
          VATRate: selectedChequeItem.VATRate || 0,
          VATAmount: selectedChequeItem.VATAmount || 0,
          RoundOff: selectedChequeItem.RoundOff || 0,
          TotalAmount: selectedChequeItem.TotalAmount,
          Remarks: selectedChequeItem.Remarks,
          IsActive: selectedChequeItem.IsActive,
          CreatedBy: selectedChequeItem.CreatedBy,
          UpdatedBy: userId,
          CreatedDate: selectedChequeItem.CreatedDate,
          UpdatedDate: new Date().toISOString()
        }
      };

      await updateData('PaymentAndReceiptApi', selectedChequeItem.Id, updateData, token);

      const statusText = newChequeStatus === 1 ? 'Pending' :
        newChequeStatus === 2 ? 'Passed' :
          newChequeStatus === 3 ? 'Bounced' : 'Cancelled';

      setSuccess(`Cheque status updated to "${statusText}" successfully!`);
      setChequeStatusModalOpen(false);
      setSelectedChequeItem(null);
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error updating cheque status:', error);
      setError('Failed to update cheque status.');
    } finally {
      setLoading(false);
    }
  };

  // Handle bulk cheque status update with individual statuses
  const handleBulkUpdateChequeStatus = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Updating cheque statuses...');

      const updatePromises = selectedChequeItems.map(async (item) => {
        const individualStatus = individualChequeStatuses[item.Id] || newChequeStatus;

        const updatePayload = {
          PaymentAndReceipt: {
            Id: item.Id,
            CompanyId: companyId,
            BranchId: branchId,
            MasterId: item.MasterId,
            SubHeadId: item.SubHeadId || 0,
            PartyId: item.PartyId || item.MasterId,
            BillDate: item.BillDate,
            BillNumber: item.BillNumber,
            FinYear: item.FinYear || new Date().getFullYear().toString(),
            IsType: item.IsType,
            IsVAT: item.IsVAT || false,
            VATHeadId: item.VATHeadId || 0,
            IsSaleAndPurchase: item.IsSaleAndPurchase || true,
            PaymentType: item.PaymentType,
            ChequeDate: item.ChequeDate,
            ChequeNumber: item.ChequeNumber,
            BankId: item.BankId,
            chequeDetails: item.chequeDetails,
            ChequeStatusId: individualStatus, // Use individual status for each item
            Amount: item.Amount,
            VATRate: item.VATRate || 0,
            VATAmount: item.VATAmount || 0,
            RoundOff: item.RoundOff || 0,
            TotalAmount: item.TotalAmount,
            Remarks: item.Remarks,
            IsActive: item.IsActive,
            CreatedBy: item.CreatedBy,
            UpdatedBy: userId,
            CreatedDate: item.CreatedDate,
            UpdatedDate: new Date().toISOString()
          }
        };

        return updateData('PaymentAndReceiptApi', item.Id, updatePayload, token);
      });

      await Promise.all(updatePromises);

      setSuccess(`${selectedChequeItems.length} cheque statuses updated successfully!`);
      setBulkChequeStatusModalOpen(false);
      setSelectedChequeItems([]);
      setIndividualChequeStatuses({});
      await fetchPaymentReceipts();
    } catch (error) {
      console.error('Error updating cheque statuses:', error);
      setError('Failed to update cheque statuses.');
    } finally {
      setLoading(false);
    }
  };

  // Open cheque status update modal
  const handleOpenChequeStatusModal = (item) => {
    setSelectedChequeItem(item);
    setNewChequeStatus(item.ChequeStatusId || 1);
    setChequeStatusModalOpen(true);
  };

  // Get cheque payment method ID dynamically
  const getChequePaymentMethodId = () => {
    console.log('=== GETTING CHEQUE PAYMENT METHOD ID ===');
    console.log('Available payment methods:', paymentMethods);

    const chequeMethod = paymentMethods.find(pm =>
      pm.Name && pm.Name.toLowerCase().includes('cheque')
    );

    console.log('Found cheque method:', chequeMethod);

    const chequeId = chequeMethod ? chequeMethod.Id : 2;
    console.log('Using cheque payment method ID:', chequeId);
    console.log('=== END CHEQUE METHOD DEBUG ===');

    return chequeId;
  };

  // Handle individual cheque status update (single item)
  const handleIndividualChequeStatusUpdate = async (item, newStatus) => {
    try {
      setLoading(true);
      setLoadingMessage(`Updating cheque status for ${item.BillNumber}...`);

      const updatePayload = {
        PaymentAndReceipt: {
          Id: item.Id,
          CompanyId: companyId,
          BranchId: branchId,
          MasterId: item.MasterId,
          SubHeadId: item.SubHeadId || 0,
          PartyId: item.PartyId || item.MasterId,
          BillDate: item.BillDate,
          BillNumber: item.BillNumber,
          FinYear: item.FinYear || new Date().getFullYear().toString(),
          IsType: item.IsType,
          IsVAT: item.IsVAT || false,
          VATHeadId: item.VATHeadId || 0,
          IsSaleAndPurchase: item.IsSaleAndPurchase || true,
          PaymentType: item.PaymentType,
          ChequeDate: item.ChequeDate,
          ChequeNumber: item.ChequeNumber,
          BankId: item.BankId,
          chequeDetails: item.chequeDetails,
          ChequeStatusId: newStatus,
          Amount: item.Amount,
          VATRate: item.VATRate || 0,
          VATAmount: item.VATAmount || 0,
          RoundOff: item.RoundOff || 0,
          TotalAmount: item.TotalAmount,
          Remarks: item.Remarks,
          IsActive: item.IsActive,
          CreatedBy: item.CreatedBy,
          UpdatedBy: userId,
          CreatedDate: item.CreatedDate,
          UpdatedDate: new Date().toISOString()
        }
      };

      await updateData('PaymentAndReceiptApi', item.Id, updatePayload, token);

      const statusText = newStatus === 1 ? 'Pending' :
        newStatus === 2 ? 'Passed' :
          newStatus === 3 ? 'Bounced' : 'Cancelled';

      setSuccess(`Cheque status updated to "${statusText}" for ${item.BillNumber}!`);

      // Update the individual status in state
      setIndividualChequeStatuses(prev => ({
        ...prev,
        [item.Id]: newStatus
      }));

      // Refresh the main data
      await fetchPaymentReceipts();

    } catch (error) {
      console.error('Error updating individual cheque status:', error);
      setError(`Failed to update cheque status for ${item.BillNumber}.`);
    } finally {
      setLoading(false);
    }
  };

  // Handle individual cheque status change
  const handleIndividualStatusChange = (itemId, newStatus) => {
    setIndividualChequeStatuses(prev => ({
      ...prev,
      [itemId]: newStatus
    }));
  };

  // Get status color and text
  const getStatusInfo = (statusId) => {
    switch (statusId) {
      case 1:
        return { text: 'Pending', color: '#f59e0b', bgColor: '#fef3c7', icon: '⏳' };
      case 2:
        return { text: 'Passed', color: '#10b981', bgColor: '#d1fae5', icon: '✅' };
      case 3:
        return { text: 'Bounced', color: '#ef4444', bgColor: '#fee2e2', icon: '❌' };
      case 4:
        return { text: 'Cancelled', color: '#6b7280', bgColor: '#f3f4f6', icon: '🚫' };
      default:
        return { text: 'Unknown', color: '#6b7280', bgColor: '#f3f4f6', icon: '❓' };
    }
  };

  // Open bulk cheque status update modal
  const handleOpenBulkChequeStatusModal = () => {
    console.log('=== OPENING BULK CHEQUE STATUS MODAL ===');

    const chequePaymentMethodId = getChequePaymentMethodId();
    console.log('Filtering items with PaymentType:', chequePaymentMethodId);
    console.log('Total filtered data items:', filteredData.length);

    const chequeItems = filteredData.filter(item => {
      const isPaymentTypeMatch = item.PaymentType === chequePaymentMethodId;
      const isActive = item.IsActive;
      const isStatusUpdateable = item.ChequeStatusId === null || item.ChequeStatusId === 0 || item.ChequeStatusId === 1;
      const isMatch = isPaymentTypeMatch && isActive && isStatusUpdateable;

      console.log(`Item ${item.Id} (${item.BillNumber}): PaymentType=${item.PaymentType}, IsActive=${item.IsActive}, ChequeStatusId=${item.ChequeStatusId}, Updateable=${isStatusUpdateable}, Match=${isMatch}`);
      return isMatch;
    });

    console.log('Found cheque items:', chequeItems.length);
    console.log('Cheque items details:', chequeItems);

    if (chequeItems.length === 0) {
      console.log('No updateable cheque items found - showing error');
      setError('No pending cheque payments found to update. Only cheques with Pending status can be updated.');
      return;
    }

    setSelectedChequeItems(chequeItems);
    setNewChequeStatus(2); // Default to "Passed"

    // Initialize individual statuses with default value
    const initialStatuses = {};
    chequeItems.forEach(item => {
      initialStatuses[item.Id] = item.ChequeStatusId || 1; // Use current status or default to "Pending"
    });
    setIndividualChequeStatuses(initialStatuses);

    console.log('Initial statuses:', initialStatuses);
    console.log('=== END MODAL OPENING DEBUG ===');

    setBulkChequeStatusModalOpen(true);
  };

  // Filter and search logic
  const filteredData = paymentReceipts.filter(item => {
    const matchesSearch = !searchTerm ||
      item.BillNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Remarks?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && item.IsActive) ||
      (filterStatus === 'inactive' && !item.IsActive);

    const matchesType = filterType === 'all' ||
      (filterType === 'payment' && item.IsType) ||
      (filterType === 'receipt' && !item.IsType);

    return matchesSearch && matchesStatus && matchesType;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const formatCurrency = (amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPaymentTypeText = (paymentType) => {
    const paymentMethod = paymentMethods.find(pm => pm.Id === paymentType);
    return paymentMethod ? paymentMethod.Name : 'Cash';
  };

  // Handle expand row functionality - REMOVED (not needed)

  return (
    <div style={{
      minWidth: '100%',
      background: '#f5f7fa',
      padding: '10px',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        padding: '12px',
        position: 'relative',
        color: 'black',
        fontFamily: 'Times New Romans',
      }}>
        {loading && <LoadingOverlay open={loading} message={loadingMessage} />}

        <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Snackbar>
        <Snackbar open={!!success} autoHideDuration={3000} onClose={() => setSuccess('')} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>
        </Snackbar>

        {/* Header Section with Title */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          paddingBottom: '8px',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Payment sx={{ fontSize: 24, color: '#667eea' }} />
            <div>
              <h4 style={{
                fontSize: '17px',
                fontWeight: '700',
                margin: 0,
                letterSpacing: '-0.3px',
                fontFamily: 'Times New Romans',
                textTransform: 'uppercase'
              }}>
                <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Payment & Receipts Management</span>
              </h4>
            </div>
          </div>
        </div>

        {/* Search Bar, Date Filters, and Buttons */}
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'center',
          marginBottom: '12px',
          flexWrap: 'wrap'
        }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by Voucher No, Master..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterList style={{ color: '#667eea', fontSize: '20px' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: '200px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
                '&:hover fieldset': {
                  borderColor: '#667eea',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#667eea',
                },
              },
            }}
          />

          {/* Date Filters */}
          <TextField
            type="date"
            size="small"
            label="From Date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: '150px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
              },
            }}
          />

          <TextField
            type="date"
            size="small"
            label="To Date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: '150px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
              },
            }}
          />

          {/* Status Filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              label="Status"
              onChange={(e) => setFilterStatus(e.target.value)}
              sx={{
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
              }}
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          {/* Type Filter */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              label="Type"
              onChange={(e) => setFilterType(e.target.value)}
              sx={{
                borderRadius: '8px',
                backgroundColor: '#f8f9fa',
                fontSize: '13px',
                height: '29px',
              }}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="payment">Payment</MenuItem>
              <MenuItem value="receipt">Receipt</MenuItem>
            </Select>
          </FormControl>

          {/* Refresh Button */}
          <Button
            variant="contained"
            onClick={fetchAllData}
            disabled={loading}
            startIcon={<Refresh />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textTransform: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              padding: '4px 16px',
              height: '29px',
              boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 4px 8px rgba(102, 126, 234, 0.4)',
              },
            }}
          >
            Refresh
          </Button>

          {/* Update Cheque Status Button */}
          <Button
            variant="contained"
            onClick={handleOpenBulkChequeStatusModal}
            startIcon={<Payment />}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textTransform: 'none',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: 600,
              padding: '4px 16px',
              height: '29px',
              boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                boxShadow: '0 4px 8px rgba(102, 126, 234, 0.4)',
              },
            }}
          >
            Cheque Status Update
          </Button>

          {/* Create Button */}
          {hasCreatePermission && (
            <Button
              variant="contained"
              onClick={() => handleOpenModal('create')}
              startIcon={<Add />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textTransform: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: 600,
                padding: '4px 16px',
                height: '29px',
                boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  boxShadow: '0 4px 8px rgba(102, 126, 234, 0.4)',
                },
              }}
            >
              Create Payment/Receipt
            </Button>
          )}
        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            borderRadius: '10px',
            overflow: 'hidden'
          }}>
            <Table sx={{ minWidth: 650 }} aria-label="payment receipts table">
              <TableHead>
                <TableRow style={{
                  background: 'linear-gradient(135deg, #612261ff 0%, #764ba2 100%)',
                  height: '27px'
                }}>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Sl No.
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Bill No
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Bill Date
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Type
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Master
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Total Amount
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Cheque Status
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'left',
                    height: '27px'
                  }}>
                    Status
                  </TableCell>
                  <TableCell style={{
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '4px 12px',
                    textAlign: 'center',
                    height: '27px'
                  }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                      No payment receipts found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.Id} style={{
                      backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                      '&:hover': { backgroundColor: '#f3f4f6' }
                    }}>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px', color: '#374151' }}>
                        {startIndex + index + 1}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px', color: '#374151', fontWeight: 600 }}>
                        {item.BillNumber}/{item.FinYear}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px', color: '#374151' }}>
                        {formatDate(item.BillDate)}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px' }}>
                        <Chip
                          label={item.IsType ? 'Payment' : 'Receipt'}
                          sx={{
                            backgroundColor: item.IsType ? '#fee2e2' : '#d1fae5',
                            color: item.IsType ? '#dc2626' : '#065f46',
                            fontSize: '10px',
                            height: '20px',
                            borderRadius: '10px',
                            fontWeight: 500
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px', color: '#374151' }}>
                        {item.MasterName || '-'}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px', color: '#374151', fontWeight: 600 }}>
                        {formatCurrency(item.TotalAmount)}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px' }}>
                        {item.PaymentType === getChequePaymentMethodId() ? (
                          <Chip
                            label={
                              item.ChequeStatusId === 1 ? 'Pending' :
                                item.ChequeStatusId === 2 ? 'Passed' :
                                  item.ChequeStatusId === 3 ? 'Bounced' :
                                    item.ChequeStatusId === 4 ? 'Cancelled' : 'Unknown'
                            }
                            color={
                              item.ChequeStatusId === 2 ? 'success' :
                                item.ChequeStatusId === 3 ? 'error' :
                                  item.ChequeStatusId === 4 ? 'default' : 'warning'
                            }
                            size="small"
                            sx={{ fontSize: '10px', height: '20px', borderRadius: '10px', fontWeight: 500 }}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', fontSize: '12px' }}>
                        <Chip
                          label={item.IsActive ? 'Active' : 'Inactive'}
                          color={item.IsActive ? 'success' : 'default'}
                          size="small"
                          sx={{ fontSize: '10px', height: '20px', borderRadius: '10px', fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                          {item.IsActive ? (
                            <>
                              <Tooltip title="View Details" arrow>
                                <Button
                                  size="small"
                                  onClick={() => handleOpenModal('view', item)}
                                  style={{
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    padding: '2px',
                                    boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                                    border: 'none',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  👁️
                                </Button>
                              </Tooltip>

                              <Tooltip title="Print" arrow>
                                <Button
                                  size="small"
                                  onClick={() => handlePrintPaymentReceipt(item.Id)}
                                  style={{
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    backgroundColor: '#8b5cf6',
                                    color: 'white',
                                    padding: '2px',
                                    boxShadow: '0 1px 4px rgba(139, 92, 246, 0.3)',
                                    border: 'none',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  🖨️
                                </Button>
                              </Tooltip>

                              {hasEditPermission && (
                                <Tooltip title="Edit" arrow>
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenModal('edit', item)}
                                    style={{
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '6px',
                                      color: 'white',
                                      padding: '2px',
                                      boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                                      border: 'none',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    ✏️
                                  </Button>
                                </Tooltip>
                              )}

                              {hasDeletePermission && (
                                <Tooltip title="Delete" arrow>
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenDeleteModal(item.Id, item.BillNumber)}
                                    style={{
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '6px',
                                      color: 'white',
                                      padding: '2px',
                                      boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                                      border: 'none',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    🗑️
                                  </Button>
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <>
                              <Tooltip title="View Details" arrow>
                                <Button
                                  size="small"
                                  onClick={() => handleOpenModal('view', item)}
                                  style={{
                                    minWidth: '22px',
                                    height: '22px',
                                    borderRadius: '6px',
                                    color: 'white',
                                    padding: '2px',
                                    boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
                                    border: 'none',
                                    fontSize: '10px',
                                    fontWeight: '600'
                                  }}
                                >
                                  👁️
                                </Button>
                              </Tooltip>

                              {hasEditPermission && (
                                <Tooltip title="Activate" arrow>
                                  <Button
                                    size="small"
                                    onClick={() => handleOpenActivateModal(item.Id, item.BillNumber)}
                                    style={{
                                      minWidth: '22px',
                                      height: '22px',
                                      borderRadius: '6px',
                                      backgroundColor: '#10b981',
                                      color: 'white',
                                      padding: '2px',
                                      boxShadow: '0 1px 4px rgba(16, 185, 129, 0.3)',
                                      border: 'none',
                                      fontSize: '10px',
                                      fontWeight: '600'
                                    }}
                                  >
                                    ✓
                                  </Button>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <div className="pagination-section">
          <div className="pagination-info">
            <div className="rows-per-page">
              <span>Rows per page:</span>
              <select
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <span>
              {startIndex + 1}-{Math.min(startIndex + rowsPerPage, filteredData.length)} of {filteredData.length}
            </span>
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              );
            })}
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </button>
          </div>
        </div>

        {/* Modal */}
        <PaymentReceiptsModal
          open={openModal}
          onClose={handleCloseModal}
          modalAction={modalAction}
          formData={formData}
          setFormData={setFormData}
          paymentReceiptDetails={paymentReceiptDetails}
          setPaymentReceiptDetails={setPaymentReceiptDetails}
          onSave={handleSave}
          onPrint={handlePrintPaymentReceipt}
          masters={masters}
          banks={banks}
          purchases={purchases}
          sales={sales}
          paymentMethods={paymentMethods}
          vatRates={vatRates}
          loading={modalLoading}
          companyId={companyId}
          token={token}
          fetchPurchases={fetchPurchases}
          fetchSales={fetchSales}
          fetchBillsForMaster={fetchBillsForMaster}
          fetchMaxBillNo={fetchMaxBillNo}
          branchId={branchId}
          fromDate={fromDate}
          toDate={toDate}
        />

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete Payment/Receipt "{deleteItemNo}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteModalOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Activate Confirmation Modal */}
        <Dialog open={activateModalOpen} onClose={() => setActivateModalOpen(false)}>
          <DialogTitle>Confirm Activate</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to activate Payment/Receipt "{activateItemNo}"?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivateModalOpen(false)}>Cancel</Button>
            <Button onClick={handleActivate} color="success" variant="contained">
              Activate
            </Button>
          </DialogActions>
        </Dialog>

        {/* Individual Cheque Status Update Modal */}
        <Dialog open={chequeStatusModalOpen} onClose={() => setChequeStatusModalOpen(false)}>
          <DialogTitle>Update Cheque Status</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>
              Update cheque status for Payment/Receipt "{selectedChequeItem?.BillNumber}"
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Cheque Number: {selectedChequeItem?.ChequeNumber || 'N/A'}
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Amount: {formatCurrency(selectedChequeItem?.Amount || 0)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Current Status: {
                  selectedChequeItem?.ChequeStatusId === 1 ? 'Pending' :
                    selectedChequeItem?.ChequeStatusId === 2 ? 'Passed' :
                      selectedChequeItem?.ChequeStatusId === 3 ? 'Bounced' :
                        selectedChequeItem?.ChequeStatusId === 4 ? 'Cancelled' : 'Unknown'
                }
              </Typography>
            </Box>
            <FormControl fullWidth>
              <InputLabel>New Cheque Status</InputLabel>
              <Select
                value={newChequeStatus}
                onChange={(e) => setNewChequeStatus(e.target.value)}
                label="New Cheque Status"
              >
                <MenuItem value={1}>Pending</MenuItem>
                <MenuItem value={2}>Passed</MenuItem>
                <MenuItem value={3}>Bounced</MenuItem>
                <MenuItem value={4}>Cancelled</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setChequeStatusModalOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateChequeStatus} color="primary" variant="contained">
              Update Status
            </Button>
          </DialogActions>
        </Dialog>

        {/* Enhanced Bulk Cheque Status Update Modal - Compact Version */}
        <Dialog
          open={bulkChequeStatusModalOpen}
          onClose={() => setBulkChequeStatusModalOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: '12px',
              boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
            }
          }}
        >
          <DialogTitle sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textAlign: 'center',
            fontSize: '1.2rem',
            fontWeight: 600,
            py: 1.5,
            px: 2
          }}>
            🏦 Cheque Status Manager
          </DialogTitle>

          <DialogContent sx={{ p: 0 }}>
            {/* Header Info */}
            <Box sx={{
              background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
              p: 1.5,
              borderBottom: '1px solid #e2e8f0'
            }}>
              <Typography variant="subtitle1" sx={{
                color: '#1e293b',
                fontWeight: 600,
                mb: 0.5,
                fontSize: '0.95rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}>
                📋 Update {selectedChequeItems.length} Cheque Payment{selectedChequeItems.length > 1 ? 's' : ''}
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                Select status for each payment or use quick actions
              </Typography>
            </Box>

            {/* Compact Bulk Actions Bar */}
            <Box sx={{
              p: 1.5,
              background: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>
                Quick:
              </Typography>
              {[
                { id: 1, text: 'Pending', color: '#f59e0b', icon: '⏳' },
                { id: 2, text: 'Passed', color: '#10b981', icon: '✅' },
                { id: 3, text: 'Bounced', color: '#ef4444', icon: '❌' },
                { id: 4, text: 'Cancelled', color: '#6b7280', icon: '🚫' }
              ].map((status) => (
                <Button
                  key={status.id}
                  size="small"
                  onClick={() => {
                    const newStatuses = {};
                    selectedChequeItems.forEach(item => {
                      newStatuses[item.Id] = status.id;
                    });
                    setIndividualChequeStatuses(newStatuses);
                  }}
                  sx={{
                    background: `${status.color}20`,
                    color: status.color,
                    border: `1px solid ${status.color}40`,
                    borderRadius: '6px',
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    minHeight: '24px',
                    px: 1,
                    py: 0.25,
                    '&:hover': {
                      background: `${status.color}30`,
                    }
                  }}
                >
                  {status.icon} {status.text}
                </Button>
              ))}
            </Box>

            {/* Compact Individual Items List */}
            <Box sx={{ maxHeight: 300, overflow: 'auto', p: 1.5 }}>
              {selectedChequeItems.map((item, index) => {
                const currentStatus = individualChequeStatuses[item.Id] || item.ChequeStatusId || 1;
                const statusInfo = getStatusInfo(currentStatus);

                return (
                  <Box
                    key={item.Id}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      background: 'white',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        borderColor: '#3b82f6'
                      }
                    }}
                  >
                    {/* Compact Item Header */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1
                    }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{
                          fontWeight: 600,
                          color: '#1e293b',
                          fontSize: '0.85rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mb: 0.5
                        }}>
                          🧾 {item.BillNumber}
                          <Chip
                            label={item.IsType ? 'Payment' : 'Receipt'}
                            size="small"
                            sx={{
                              background: item.IsType ? '#fee2e2' : '#d1fae5',
                              color: item.IsType ? '#dc6363' : '#065f46',
                              fontSize: '0.65rem',
                              height: '16px'
                            }}
                          />
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                            📅 {formatDate(item.BillDate)}
                          </Typography>
                          {item.ChequeNumber && (
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                              🏦 {item.ChequeNumber}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.7rem' }}>
                            👤 {masters.find(m => m.Id === item.MasterId)?.Name || 'Unknown'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="subtitle2" sx={{
                          fontWeight: 700,
                          color: '#1e293b',
                          fontSize: '0.9rem'
                        }}>
                          {formatCurrency(item.TotalAmount || item.Amount)}
                        </Typography>
                        <Chip
                          label={`${statusInfo.icon} ${statusInfo.text}`}
                          size="small"
                          sx={{
                            background: statusInfo.bgColor,
                            color: statusInfo.color,
                            fontWeight: 600,
                            fontSize: '0.65rem',
                            height: '18px'
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Compact Status Selector with Individual Update - Same Line */}
                    <Box sx={{
                      background: '#f8fafc',
                      borderRadius: '6px',
                      p: 1,
                      border: '1px solid #e2e8f0'
                    }}>
                      <Typography variant="caption" sx={{
                        fontWeight: 600,
                        color: '#374151',
                        mb: 0.5,
                        fontSize: '0.75rem',
                        display: 'block'
                      }}>
                        🎯 Select Status:
                      </Typography>

                      {/* Status buttons and Update button in same line */}
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap'
                      }}>
                        {/* Status Selection Buttons - Left Side */}
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {[
                            { id: 1, text: 'Pending', color: '#f59e0b', bgColor: '#fef3c7', icon: '⏳' },
                            { id: 2, text: 'Passed', color: '#10b981', bgColor: '#d1fae5', icon: '✅' },
                            { id: 3, text: 'Bounced', color: '#ef4444', bgColor: '#fee2e2', icon: '❌' },
                            { id: 4, text: 'Cancelled', color: '#6b7280', bgColor: '#f3f4f6', icon: '🚫' }
                          ].map((status) => (
                            <Button
                              key={status.id}
                              size="small"
                              variant={currentStatus === status.id ? 'contained' : 'outlined'}
                              onClick={() => handleIndividualStatusChange(item.Id, status.id)}
                              sx={{
                                background: currentStatus === status.id ? status.color : 'transparent',
                                color: currentStatus === status.id ? 'white' : status.color,
                                border: `1px solid ${status.color}`,
                                borderRadius: '4px',
                                fontSize: '0.65rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                minWidth: '70px',
                                minHeight: '24px',
                                px: 0.5,
                                py: 0.25,
                                '&:hover': {
                                  background: currentStatus === status.id ? status.color : `${status.color}10`,
                                }
                              }}
                            >
                              {status.icon} {status.text}
                            </Button>
                          ))}
                        </Box>

                        {/* Individual Update Button - Right Side */}
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleIndividualChequeStatusUpdate(item, currentStatus)}
                          disabled={
                            currentStatus === (item.ChequeStatusId || 1) ||
                            (item.ChequeStatusId !== null && item.ChequeStatusId !== 0 && item.ChequeStatusId !== 1)
                          }
                          sx={{
                            background: 'linear-gradient(135deg, #059669, #047857)',
                            color: 'white',
                            borderRadius: '4px',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            minHeight: '26px',
                            px: 1.5,
                            boxShadow: '0 1px 3px rgba(5, 150, 105, 0.3)',
                            flexShrink: 0, // Prevent shrinking
                            '&:hover': {
                              background: 'linear-gradient(135deg, #047857, #065f46)',
                              boxShadow: '0 2px 4px rgba(5, 150, 105, 0.4)'
                            },
                            '&:disabled': {
                              background: '#e5e7eb',
                              color: '#9ca3af',
                              boxShadow: 'none'
                            }
                          }}
                        >
                          💾 Update This Item
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </DialogContent>

          <DialogActions sx={{
            p: 1.5,
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderTop: '1px solid #e2e8f0',
            gap: 1,
            flexDirection: 'column'
          }}>
            {/* Bulk Actions Info */}
            <Box sx={{
              width: '100%',
              textAlign: 'center',
              mb: 1,
              p: 1,
              background: '#e0f2fe',
              borderRadius: '6px',
              border: '1px solid #0284c7'
            }}>
              <Typography variant="caption" sx={{
                color: '#0284c7',
                fontWeight: 600,
                fontSize: '0.75rem',
                display: 'block'
              }}>
                💡 Tip: Use individual "Update This Item" buttons above, or use "Update All" below for bulk changes
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'center' }}>
              <Button
                onClick={() => {
                  setBulkChequeStatusModalOpen(false);
                  setIndividualChequeStatuses({});
                }}
                sx={{
                  color: '#6b7280',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  py: 0.5,
                  fontSize: '0.8rem',
                  '&:hover': {
                    background: '#f3f4f6',
                    borderColor: '#9ca3af'
                  }
                }}
              >
                ❌ Cancel
              </Button>

              <Button
                onClick={handleBulkUpdateChequeStatus}
                variant="contained"
                disabled={selectedChequeItems.length === 0}
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 2.5,
                  py: 0.5,
                  fontSize: '0.8rem',
                  boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb, #1e40af)',
                    boxShadow: '0 4px 6px rgba(59, 130, 246, 0.4)'
                  },
                  '&:disabled': {
                    background: '#e5e7eb',
                    color: '#9ca3af'
                  }
                }}
              >
                🔄 Update All ({selectedChequeItems.length}) with Selected Status
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Success/Error Snackbars */}
        <Snackbar
          open={!!success}
          autoHideDuration={6000}
          onClose={() => setSuccess('')}
        >
          <Alert onClose={() => setSuccess('')} severity="success">
            {success}
          </Alert>
        </Snackbar>

        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={() => setError('')}
        >
          <Alert onClose={() => setError('')} severity="error">
            {error}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

export default PaymentReceiptsIndex;